import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

function displayValueFor(cents) {
  if (cents === null || cents === undefined || cents === '') return ''

  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  })
}

function editableValueFor(cents) {
  if (cents === null || cents === undefined || cents === '') return ''

  return (cents / 100).toFixed(2)
}

function centsFrom(value) {
  const numericValue = value.replace(/[^0-9.]/g, '')
  if (numericValue === '') return null

  const [dollars, ...centParts] = numericValue.split('.')
  const dollarsInCents = Number.parseInt(dollars || '0', 10) * 100
  const cents = Number.parseInt((centParts.join('') || '').slice(0, 2).padEnd(2, '0'), 10) || 0

  return dollarsInCents + cents
}

function CurrencyInput({
  className,
  label,
  helpText,
  errorMessage,
  id,
  onChange,
  value,
  max = Number.MAX_SAFE_INTEGER,
  required,
  disabled,
  onBlur,
  onFocus,
  ...rest
}) {
  const [displayValue, setDisplayValue] = useState(displayValueFor(value))
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    if (!focused) setDisplayValue(displayValueFor(value))
  }, [focused, value])

  const handleFocus = useCallback(
    (e) => {
      setFocused(true)
      setDisplayValue(editableValueFor(value))
      onFocus?.(e)
    },
    [onFocus, value]
  )

  const handleBlur = useCallback(
    (e) => {
      setFocused(false)
      setDisplayValue(displayValueFor(centsFrom(e.target.value)))
      onBlur?.(e)
    },
    [onBlur]
  )

  const handleChange = useCallback(
    (e) => {
      const nextValue = centsFrom(e.target.value)
      if (nextValue !== null && nextValue > max) return

      setDisplayValue(e.target.value)
      onChange?.(nextValue)
    },
    [max, onChange]
  )

  if (!id) throw new Error('id is required')

  return (
    <div className={className}>
      {label && (
        <label
          className={`${classNames({
            'border-red-500': errorMessage,
            'text-red-500': errorMessage
          })} block text-gray-800 text-sm font-medium ml-1`}
          htmlFor={id}
        >
          {label}
          {required && '*'}
        </label>
      )}
      {helpText && <p className="text-gray-500 ml-1 text-xs">{helpText}</p>}
      <input
        {...rest}
        className={`${classNames({
          'text-red-500 border-red-500 placeholder-red-300 ring ring-red-500 ring-opacity-50 error-shake': errorMessage,
          'text-gray-800 placeholder-gray-500': !errorMessage,
          'shadow-inner': !disabled,
          'bg-gray-100': disabled
        })} text-sm border rounded w-full py-2 px-3 leading-tight focus:ring focus:border-purple-500 focus:ring-purple-300 focus:ring-opacity-50 border-gray-300 mt-2`}
        inputMode="decimal"
        id={id}
        disabled={disabled}
        required={required}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        data-cy={`${id}-currency-input`}
        value={displayValue}
      />
      <p className="mt-1 mb-3 text-xs italic text-red-500">{errorMessage}</p>
    </div>
  )
}

CurrencyInput.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  value: PropTypes.any,
  label: PropTypes.string,
  helpText: PropTypes.string,
  className: PropTypes.string,
  errorMessage: PropTypes.string,
  max: PropTypes.number,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool
}

export default CurrencyInput
